import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import { exportTable } from "../../../utils/exportTable";
import { toast } from "../../../components/Toast/toast";
import { btn } from "../../../components/Button/buttonStyles";

const fmtISO = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  const hh = String(dt.getHours()).padStart(2, "0");
  const min = String(dt.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
};

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "0.00";

const HEADERS = [
  "Code",
  "Linked Invoice",
  "Coverage by Invoices",
  "Client",
  "Project",
  "Amount",
  "Linked Amount",
  "Unlinked Amount",
  "Currency",
  "Paid At",
];

const COVERAGE_CLS = {
  "Fully covered":     "text-green-600 font-medium",
  "Partially covered": "text-amber-600 font-medium",
  "Not covered":       "text-danger-500 font-medium",
};

const ClientPaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 50 });
  const [exporting, setExporting] = useState(false);

  const fetchPayments = async (page = 1) => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/project-finances/client-payments", {
        params: { page, limit: 50 },
        withCredentials: true,
      });
      setPayments(res.data.data || []);
      setMeta(res.data.meta || { total: 0, page: 1, limit: 50 });
    } catch (err) {
      console.error(err);
      setError("Failed to fetch payments.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const getClientName = (p) => {
    const c = p.client;
    if (!c) return "—";
    if (c.type?.toLowerCase() === "company") return c.company_name || "—";
    if (c.primary_user)
      return `${c.primary_user.first_name} ${c.primary_user.last_name}`.trim();
    return "—";
  };

  const getCurrencyCode = (p) =>
    p.currency?.currency?.code || "—";

  // Mirrors the table columns below, in the same order.
  const EXPORT_COLUMNS = [
    { header: "Code", value: (p) => p.payment_code || `CP${p.id}` },
    { header: "Linked Invoice", value: (p) => p.linkedInvoice?.invoice_code || "" },
    { header: "Coverage by Invoices", value: (p) => p.coverage_by_invoices || "" },
    { header: "Client", value: (p) => getClientName(p) },
    { header: "Project", value: (p) => p.project?.project_name || "" },
    { header: "Amount", value: (p) => fmtNum(p.amount) },
    { header: "Linked Amount", value: (p) => fmtNum(p.linked_amount) },
    { header: "Unlinked Amount", value: (p) => fmtNum(p.unlinked_amount) },
    { header: "Currency", value: (p) => getCurrencyCode(p) },
    { header: "Paid At", value: (p) => fmtISO(p.paid_at) },
  ];

  // The table is paginated, so the export pulls every row rather than the page
  // currently on screen.
  const handleExport = async () => {
    try {
      setExporting(true);
      const res = await api.get("/project-finances/client-payments", {
        params: { page: 1, limit: 100000 },
        withCredentials: true,
      });
      exportTable("client-payments", EXPORT_COLUMNS, res.data.data || []);
    } catch (err) {
      console.error("Export failed", err);
      toast.error("Could not export the table.");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.ceil(meta.total / meta.limit);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Client Payments</h1>
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={handleExport}
            disabled={exporting}
            className={btn("secondary", "sm")}
          >
            {exporting ? "Exporting..." : "Export to Excel"}
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
        </div>
      )}
      {error && <p className="text-danger-600 text-sm">{error}</p>}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{meta.total.toLocaleString()}</span> items.
          </p>

          <div className="w-full bg-white shadow rounded-lg overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-auto border-collapse min-w-[1300px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    {HEADERS.map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr>
                      <td
                        colSpan={HEADERS.length}
                        className="py-10 text-center text-gray-400 text-xs"
                      >
                        No payments found.
                      </td>
                    </tr>
                  ) : (
                    payments.map((p, index) => {
                      const rowBg =
                        index % 2 === 0 ? "bg-gray-50" : "bg-white";
                      const coverageCls =
                        COVERAGE_CLS[p.coverage_by_invoices] ?? "text-gray-600";

                      return (
                        <tr
                          key={p.id}
                          className={`${rowBg} hover:bg-brand-50 transition-colors`}
                        >
                          {/* Code */}
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            <Link
                              to={`/client/payment/${p.id}`}
                              className="text-brand-600 hover:text-brand-800 underline font-medium"
                            >
                              {p.payment_code || `CP${p.id}`}
                            </Link>
                          </td>

                          {/* Linked Invoice */}
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            {p.linkedInvoice
                              ? <Link to={`/client/invoice/${p.linkedInvoice.id}`} className="text-brand-600 hover:underline font-medium">{p.linkedInvoice.invoice_code}</Link>
                              : "—"}
                          </td>

                          {/* Coverage by Invoices */}
                          <td className={`px-3 py-2 text-xs whitespace-nowrap text-center ${coverageCls}`}>
                            {p.coverage_by_invoices || "—"}
                          </td>

                          {/* Client */}
                          <td className="px-3 py-2 text-xs whitespace-nowrap">
                            {p.client?.id ? (
                              <Link
                                to={`/clients/${p.client.id}`}
                                className="text-brand-600 hover:text-brand-800 underline font-medium"
                              >
                                {getClientName(p)}
                              </Link>
                            ) : (
                              getClientName(p)
                            )}
                          </td>

                          {/* Project */}
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-600">
                            {p.project
                              ? <Link to={`/project/${p.project.id}`} className="text-brand-600 hover:underline">{p.project.project_name}</Link>
                              : "—"}
                          </td>

                          {/* Amount */}
                          <td className="px-3 py-2 text-xs font-semibold text-right whitespace-nowrap">
                            {fmtNum(p.amount)}
                          </td>

                          {/* Linked Amount */}
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">
                            {fmtNum(p.linked_amount)}
                          </td>

                          {/* Unlinked Amount */}
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">
                            {fmtNum(p.unlinked_amount)}
                          </td>

                          {/* Currency */}
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            {getCurrencyCode(p)}
                          </td>

                          {/* Paid At */}
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                            {fmtISO(p.paid_at)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 pt-2">
              <button
                disabled={meta.page <= 1}
                onClick={() => fetchPayments(meta.page - 1)}
                className={btn("secondary", "sm")}
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {meta.page} of {totalPages}
              </span>
              <button
                disabled={meta.page >= totalPages}
                onClick={() => fetchPayments(meta.page + 1)}
                className={btn("secondary", "sm")}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ClientPaymentsPage;
